import type { DicomColor } from '../dicom/dicomColor';
import type { DICOMApprovalStatus } from '../dicom/dicomConstants';
import { DICOMApprovalStatus as ApprovalStatus } from '../dicom/dicomConstants';
import type { Image3D } from '../patient/image3d';
import type { Patient } from '../patient/patient';
import type { ApprovableRegistration } from '../patient/approvableRegistration';
import type { StructureSet } from '../patient/structureset';
import type {
    PersistedEntityId,
    PersistenceRegistry,
} from '../persistence/PersistenceRegistry';
import type {
    DomainChange,
    RegistrationSnapshot,
    RoiSnapshot,
    StructureSetSnapshot,
} from './DomainChange';
import type { DomainStore } from './DomainStore';
import type {
    DoseId,
    ImageId,
    RegistrationId,
    RoiId,
    StructureSetId,
} from './ids';

/**
 * How one ROI differs from its load-time baseline. `edited` covers DICOM
 * property changes without a dedicated snapshot field (description,
 * generation algorithm, density overrides).
 */
export type RoiChangeKind =
    | 'added'
    | 'deleted'
    | 'renamed'
    | 'colorChanged'
    | 'lockChanged'
    | 'typeChanged'
    | 'approvalChanged'
    | 'contoursEdited'
    | 'edited';

export interface PendingRoiChange {
    readonly roiId: RoiId;
    readonly roiNumber: number;
    /** For UI display only — ROI names are never logged. */
    readonly displayName: string;
    readonly kinds: ReadonlySet<RoiChangeKind>;
    /** Current display colour, matching the contour in the viewer. */
    readonly color: Readonly<DicomColor>;
    /** Load-time colour when `colorChanged`, for an old → new diff. */
    readonly previousColor: Readonly<DicomColor> | null;
    /**
     * A point ROI has no contours; the UI words its geometry edits as a
     * move ("point moved") instead of "contours edited".
     */
    readonly isPointRoi: boolean;
}

export type PendingStructureSetStatus = 'new' | 'modified' | 'deleted';

export interface PendingStructureSetChanges {
    readonly structureSetId: StructureSetId;
    /** For UI display only — never logged. */
    readonly displayName: string;
    readonly status: PendingStructureSetStatus;
    /** Total ROI count of the set (not just the changed ones). */
    readonly roiCount: number;
    /**
     * Epoch ms of the last tracked change to this set (its own metadata
     * or any of its ROIs). Display metadata only — never part of a
     * clinical calculation. Null when no changeset has been seen for the
     * set (should not happen for a dirty entry; fail-safe for display).
     */
    readonly lastModifiedAt: number | null;
    /** The stored RTSTRUCT file no longer matches: needs a content PUT. */
    readonly contentDirty: boolean;
    /** Structure-set approval status / reviewer changed: approval endpoint. */
    readonly approvalDirty: boolean;
    /**
     * Every changed ROI carries only edits the database row accepts, so the
     * save takes the per-structure metadata PATCH and leaves the stored file
     * untouched. That is the edit lock, which no RTSTRUCT can hold, plus the
     * colour while the set is approved, where a content PUT would be rejected
     * (409).
     */
    readonly rowOnly: boolean;
    readonly roiChanges: ReadonlyMap<RoiId, PendingRoiChange>;
}

export interface PendingRegistrationChanges {
    readonly registrationId: RegistrationId;
    /** For UI display only (contentLabel) — never logged. */
    readonly displayName: string;
    readonly status: 'new' | 'modified' | 'deleted';
    /** The committed matrix differs from load time (transformRevision). */
    readonly matrixDirty: boolean;
    /** The label (contentLabel) changed — persisted via the rename PATCH. */
    readonly labelDirty: boolean;
    /** The description changed — has no wire path yet (honest failure). */
    readonly descriptionDirty: boolean;
    /** Approval status / reviewer changed. */
    readonly approvalDirty: boolean;
    /** Epoch ms of the last tracked change (display metadata only). */
    readonly lastModifiedAt: number | null;
}

/**
 * A pending image or dose change. Images support creation (a locally
 * reformatted/cropped series uploads at save), label rename, and
 * deletion; doses still track deletions only (their modifications —
 * scale, normalisation, colorwash — are transient display state).
 */
export interface PendingDatasetChanges {
    readonly kind: 'image' | 'dose';
    readonly id: ImageId | DoseId;
    /** For UI display only — never logged. */
    readonly displayName: string;
    /** Kind tag for the review panel chip (CT/MR/…, or RTDOSE). */
    readonly modality: string;
    readonly status: 'new' | 'modified' | 'deleted';
    /** The series label differs from load time (images only). */
    readonly labelDirty: boolean;
    /** Epoch ms of the last tracked change (display metadata only). */
    readonly lastModifiedAt: number | null;
}

export interface PendingChangesState {
    readonly byStructureSet: ReadonlyMap<
        StructureSetId,
        PendingStructureSetChanges
    >;
    readonly byRegistration: ReadonlyMap<
        RegistrationId,
        PendingRegistrationChanges
    >;
    readonly byImage: ReadonlyMap<ImageId, PendingDatasetChanges>;
    readonly byDose: ReadonlyMap<DoseId, PendingDatasetChanges>;
    /**
     * True when anything is pending — including changes to entities the
     * save workflow does not cover yet (dose display settings, plans),
     * which keep arming the leave/close guard exactly as the legacy
     * boolean did.
     */
    readonly hasChanges: boolean;
    /** Changes outside the dataset save scope (see hasChanges). */
    readonly hasOutOfScopeChanges: boolean;
}

/**
 * What one save attempt is going to persist: the classification at
 * `beginSaveAttempt()` time plus the snapshot values that become the new
 * baselines once the save succeeds. Edits made while the save is in
 * flight bump the live snapshots past these values, so `completeSave`
 * re-dirties them against the new baselines instead of clearing them.
 */
export interface SaveIntent {
    readonly state: PendingChangesState;
    readonly baselineCandidates: {
        readonly structureSets: ReadonlyMap<
            StructureSetId,
            StructureSetSnapshot
        >;
        readonly rois: ReadonlyMap<RoiId, RoiSnapshot>;
        readonly registrations: ReadonlyMap<
            RegistrationId,
            RegistrationSnapshot
        >;
        /** Image series labels at attempt time (rename re-baselining). */
        readonly imageLabels: ReadonlyMap<ImageId, string>;
    };
}

/**
 * The slice of a save report's per-entry outcome the store consumes in
 * {@link PendingChangesStore.completeSave}. Structurally satisfied by
 * the save layer's SaveOutcome, so the orchestrator hands the report's
 * outcomes straight through — declared here so the changes layer does
 * not import the services layer.
 */
export interface SaveOutcomeSummary {
    readonly kind: 'saved' | 'conflict' | 'failed';
    readonly entity: DatasetKind;
    readonly id: string;
}

/** The dataset kinds whose lifecycle the store tracks uniformly. */
export type DatasetKind = 'structureSet' | 'registration' | 'image' | 'dose';

/**
 * One dataset's lifecycle relative to the database, shared by all four
 * kinds. Kind-specific payload state (snapshots, labels, display
 * metadata) stays in the per-kind maps; this entry is the single
 * description of the created/deleted bookkeeping for every kind.
 *
 * Invariant: an entry exists only while it says something — a flag is
 * set or a save has it in flight. Transitions that return an entry to
 * the all-clear state remove it (see pruneTracked), so "no entry" and
 * "nothing noteworthy" stay the same statement.
 */
interface TrackedEntry {
    readonly kind: DatasetKind;
    /** Created in this session (no database row at hydration). */
    createdInSession: boolean;
    /** A delete is pending — or unsendable (see hasOutOfScopeDatasets). */
    deleted: boolean;
    /**
     * Where the dataset stands relative to the save attempt in flight.
     * 'saving': part of the current attempt — a create's ref is minted
     * only once its upload commits, so between beginSaveAttempt and
     * completeSave "no ref" does not yet mean "not in the database".
     * 'savingDeleteRequested': a delete arrived in exactly that window
     * and is held until the save settles and the registry can be
     * trusted again (see resolveDelete / settleDeferredDeletes).
     */
    phase: 'idle' | 'saving' | 'savingDeleteRequested';
}

/**
 * Accumulates exact per-object dirty state for the save workflow.
 *
 * The {@link ChangeTracker} stream is transient (flushed and discarded per
 * command); this store is its accumulating consumer. It keeps a baseline
 * snapshot of every structure set / ROI / registration captured at
 * hydration, applies each changeset entry to a "latest" snapshot map, and
 * classifies dirtiness by field-diffing latest against baseline — so a
 * rename A → B → A cancels out, while counters (contourRevision,
 * transformRevision) deliberately never cancel (worst case: saving
 * identical bytes, never a missed save). Image and dose entries track
 * deletions only (they have no persistable content edits today).
 *
 * Framework-free; exposed to React via the useSyncExternalStore-shaped
 * `subscribe`/`getState`.
 *
 * Fail-safe posture: a changeset entry that cannot be attributed (unknown
 * ROI owner) still forces `hasChanges` — the indicator may over-report,
 * it must never show "nothing to save" for changed data.
 */
export class PendingChangesStore {
    private readonly ssBaseline = new Map<
        StructureSetId,
        StructureSetSnapshot
    >();
    private readonly roiBaseline = new Map<RoiId, RoiSnapshot>();
    private readonly ssLatest = new Map<StructureSetId, StructureSetSnapshot>();
    private readonly roiLatest = new Map<RoiId, RoiSnapshot>();
    /** RoiId → owning structure set, maintained across create/delete. */
    private readonly roiOwner = new Map<RoiId, StructureSetId>();
    /** Epoch ms of the last changeset entry attributed to each entity. */
    private readonly lastChangeAt = new Map<string, number>();
    private readonly createdRoi = new Set<RoiId>();
    private readonly deletedRoi = new Set<RoiId>();

    private readonly regBaseline = new Map<
        RegistrationId,
        RegistrationSnapshot
    >();
    private readonly regLatest = new Map<
        RegistrationId,
        RegistrationSnapshot
    >();
    /**
     * Per-dataset lifecycle (create/delete bookkeeping) for all four
     * dataset kinds — see {@link TrackedEntry}.
     */
    private readonly tracked = new Map<string, TrackedEntry>();
    /**
     * Image series labels, baseline vs latest — read from the live
     * aggregate (never from snapshots: image snapshots deliberately
     * exclude free text). A rename A → B → A cancels out like other
     * field diffs.
     */
    private readonly imgLabelBaseline = new Map<ImageId, string>();
    private readonly imgLabelLatest = new Map<ImageId, string>();
    /**
     * UI display metadata for image/dose entries, captured from the live
     * aggregate at baseline and on `created` entries — their snapshots
     * deliberately exclude free text (it may carry PHI and snapshots reach
     * the changeset log), so the panel names live only here. Never logged.
     */
    private readonly datasetMeta = new Map<
        string,
        { displayName: string; modality: string }
    >();
    /** Entries that could not be attributed — forces hasChanges. */
    private unattributedChanges = 0;
    /**
     * Unsaved work that no save can carry and that cannot be taken back —
     * plan edits, dose display settings, the legacy event-bus paths. A latch by
     * nature: the change happened and saving does not undo it.
     */
    private outOfScopeChanges = false;
    private readonly listeners = new Set<() => void>();
    private readonly unsubscribe: () => void;
    private cachedState: PendingChangesState | null = null;
    private disposed = false;

    constructor(
        store: DomainStore,
        private readonly registry: PersistenceRegistry,
        private readonly patient: Patient,
        /** Injectable clock for tests; display metadata only. */
        private readonly now: () => number = () => Date.now()
    ) {
        this.captureBaselines();
        this.unsubscribe = store.onChangeset((changes) => {
            this.applyChangeset(changes);
        });
    }

    dispose(): void {
        this.disposed = true;
        this.unsubscribe();
        this.listeners.clear();
    }

    /** useSyncExternalStore-shaped. */
    subscribe = (listener: () => void): (() => void) => {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    };

    getState = (): PendingChangesState => {
        if (!this.cachedState) {
            this.cachedState = this.deriveState();
        }
        return this.cachedState;
    };

    /**
     * Captures what a save attempt will persist. Call before serializing
     * blobs; pass the intent back to {@link completeSave} afterwards.
     */
    beginSaveAttempt(): SaveIntent {
        // A save that never reached completeSave (a crashed run) may have
        // left phases behind. Its deferred deletes settle with today's
        // registry answer before the intent is captured — so they ride
        // this very attempt — and stale 'saving' marks are released so
        // they cannot defer deletes for a save that is not running.
        if (this.settleDeferredDeletes()) {
            this.invalidate();
        }
        this.releasePhases();
        const state = this.getState();
        const arm = (id: string, kind: DatasetKind): void => {
            this.trackedFor(id, kind).phase = 'saving';
        };
        for (const id of state.byStructureSet.keys()) {
            arm(id, 'structureSet');
        }
        for (const id of state.byRegistration.keys()) {
            arm(id, 'registration');
        }
        for (const id of state.byImage.keys()) {
            arm(id, 'image');
        }
        for (const id of state.byDose.keys()) {
            arm(id, 'dose');
        }
        return {
            state,
            baselineCandidates: {
                structureSets: new Map(this.ssLatest),
                rois: new Map(this.roiLatest),
                registrations: new Map(this.regLatest),
                imageLabels: new Map(this.imgLabelLatest),
            },
        };
    }

    /**
     * Consumes the save report: clears pending state for every entry
     * whose outcome is 'saved', using the intent's captured snapshots as
     * the new baselines. Failed and conflicted entries stay dirty; edits
     * made during the save differ from the new baselines and re-dirty.
     */
    completeSave(
        intent: SaveIntent,
        outcomes: readonly SaveOutcomeSummary[]
    ): void {
        for (const outcome of outcomes) {
            if (outcome.kind !== 'saved') {
                continue;
            }
            this.settleSavedEntry(outcome.entity, outcome.id, intent);
        }
        this.settleDeferredDeletes();
        this.releasePhases();
        this.invalidate();
    }

    /**
     * Settles one saved entry using the intent's classification of what
     * the save carried: a saved DELETE erases the entry and its payload
     * state wholesale; a saved upsert promotes the attempt-time
     * snapshots to baselines and clears the created flag.
     */
    private settleSavedEntry(
        entity: DatasetKind,
        id: string,
        intent: SaveIntent
    ): void {
        switch (entity) {
            case 'structureSet': {
                const ssId = id as StructureSetId;
                const pending = intent.state.byStructureSet.get(ssId);
                if (!pending) {
                    return;
                }
                if (pending.status === 'deleted') {
                    this.settleSavedDelete(ssId, 'structureSet');
                    return;
                }
                const ssSnapshot =
                    intent.baselineCandidates.structureSets.get(ssId);
                if (ssSnapshot) {
                    this.ssBaseline.set(ssId, ssSnapshot);
                }
                this.clearCreatedFlag(ssId);
                for (const roiId of pending.roiChanges.keys()) {
                    if (this.deletedRoi.has(roiId)) {
                        this.forgetRoi(roiId);
                        continue;
                    }
                    const roiSnapshot =
                        intent.baselineCandidates.rois.get(roiId);
                    if (roiSnapshot) {
                        this.roiBaseline.set(roiId, roiSnapshot);
                    }
                    this.createdRoi.delete(roiId);
                }
                return;
            }
            case 'registration': {
                const regId = id as RegistrationId;
                const pending = intent.state.byRegistration.get(regId);
                if (!pending) {
                    return;
                }
                if (pending.status === 'deleted') {
                    this.settleSavedDelete(regId, 'registration');
                    return;
                }
                const snapshot =
                    intent.baselineCandidates.registrations.get(regId);
                if (snapshot) {
                    this.regBaseline.set(regId, snapshot);
                }
                this.clearCreatedFlag(regId);
                return;
            }
            case 'image': {
                const imageId = id as ImageId;
                const pending = intent.state.byImage.get(imageId);
                if (!pending || pending.status === 'deleted') {
                    this.settleSavedDelete(imageId, 'image');
                    return;
                }
                // Created/renamed image persisted: re-baseline the label
                // to the attempt-time value. datasetMeta is deliberately
                // KEPT — a later local delete of the now-persisted image
                // needs its display name, and captureImageMeta will not
                // run again for it.
                const label =
                    intent.baselineCandidates.imageLabels.get(imageId);
                if (label !== undefined) {
                    this.imgLabelBaseline.set(imageId, label);
                }
                this.clearCreatedFlag(imageId);
                return;
            }
            case 'dose':
                // Doses have no upsert path; a saved dose is a saved delete.
                this.settleSavedDelete(id as DoseId, 'dose');
                return;
        }
    }

    /**
     * A saved DELETE leaves nothing to track: the entry, its payload
     * state and its display metadata all go. Deleting the entry (rather
     * than clearing the flag) also heals the created flag a delete of a
     * failed-but-committed create keeps — see resolveDelete — so the
     * dataset cannot resurface as a pending 'new' entry after its
     * DELETE saved.
     */
    private settleSavedDelete(id: PersistedEntityId, kind: DatasetKind): void {
        this.tracked.delete(id);
        this.forgetPayload(id, kind);
        this.datasetMeta.delete(id);
    }

    /**
     * Settles deletes that arrived while their dataset was mid-save. The
     * registry can answer now: a ref means the create committed, so the row
     * exists and the delete has to be sent; no ref means it never landed and
     * the entry cancels exactly as an ordinary create-then-delete does.
     *
     * Replaying the change rather than classifying here keeps one description
     * of what a delete means — the same branches, now with an answer they can
     * trust. Returns whether anything settled (derived state changed).
     */
    private settleDeferredDeletes(): boolean {
        let settled = false;
        for (const [id, entry] of this.tracked) {
            if (entry.phase !== 'savingDeleteRequested') {
                continue;
            }
            // Idled first so the replayed delete is not deferred again.
            entry.phase = 'idle';
            settled = true;
            switch (entry.kind) {
                case 'structureSet': {
                    const snapshot = this.ssLatest.get(id as StructureSetId);
                    if (snapshot) {
                        this.applyStructureSetChange({
                            kind: 'structureSet',
                            id: id as StructureSetId,
                            type: 'deleted',
                            snapshot,
                        });
                    }
                    break;
                }
                case 'registration': {
                    const snapshot = this.regLatest.get(id as RegistrationId);
                    if (snapshot) {
                        this.applyRegistrationChange({
                            kind: 'registration',
                            id: id as RegistrationId,
                            type: 'deleted',
                            snapshot,
                        });
                    }
                    break;
                }
                case 'image':
                    this.resolveDelete(id as ImageId, 'image');
                    break;
                case 'dose':
                    this.resolveDelete(id as DoseId, 'dose');
                    break;
            }
        }
        return settled;
    }

    /** Returns every entry to 'idle', dropping the all-clear ones. */
    private releasePhases(): void {
        for (const [id, entry] of this.tracked) {
            entry.phase = 'idle';
            this.pruneTracked(id);
        }
    }

    /**
     * Arms the leave/close guard for a mutation that does not reach the
     * change tracker (legacy event-bus paths). Coarse by design — mirrors
     * the old global boolean for out-of-scope entities.
     */
    /**
     * Takes datasets the connector already holds off the pending list.
     *
     * An on-demand load brings one more of the patient's datasets into an
     * open session, and the tracker reports it the only way it can — as
     * created. It was not: the row exists, this session merely fetched it.
     * Left as a create it would sit in "unsaved changes" forever, and a save
     * would write a second copy of the row the load just read.
     *
     * Baselines come from the live objects, the same way they do for the
     * patient the session opened with. Only the ids named are touched: a
     * wholesale re-baseline would silently adopt edits the user has made and
     * not yet saved, which is the opposite of what this is for.
     */
    adoptPersisted(ids: readonly PersistedEntityId[]): void {
        for (const id of ids) {
            const ss = this.patient.structureSets.find((s) => s.id === id);
            if (ss) {
                this.baselineStructureSet(ss);
            }
            const reg = this.patient.approvableRegistrations.find(
                (r) => r.id === id
            );
            if (reg) {
                this.baselineRegistration(reg);
            }
            const image = this.patient.images.find((i) => i.id === id);
            if (image) {
                this.baselineImage(image);
            }
            const dose = this.patient.doses.find((d) => d.id === id);
            if (dose) {
                this.captureDoseMeta(dose.id);
            }
            this.clearCreatedFlag(id);
        }
        this.invalidate();
    }

    markOutOfScopeChange(): void {
        this.outOfScopeChanges = true;
        this.invalidate();
    }

    // ── Dataset lifecycle ─────────────────────────────────────────────

    private trackedFor(id: string, kind: DatasetKind): TrackedEntry {
        let entry = this.tracked.get(id);
        if (!entry) {
            entry = {
                kind,
                createdInSession: false,
                deleted: false,
                phase: 'idle',
            };
            this.tracked.set(id, entry);
        }
        return entry;
    }

    /** Drops an entry that no longer says anything (see TrackedEntry). */
    private pruneTracked(id: string): void {
        const entry = this.tracked.get(id);
        if (
            entry &&
            !entry.createdInSession &&
            !entry.deleted &&
            entry.phase === 'idle'
        ) {
            this.tracked.delete(id);
        }
    }

    private markCreated(id: string, kind: DatasetKind): void {
        const entry = this.trackedFor(id, kind);
        entry.createdInSession = true;
        entry.deleted = false;
    }

    /**
     * The one delete transition all four dataset kinds share — the
     * registry answers which of three things a delete means.
     * 'deferred': the dataset is mid-save with no ref yet, the window
     * where "no ref" does not yet mean "not in the database" — deciding
     * against that null is how a mid-upload delete loses its DELETE and
     * lets the row resurrect on reload, so the entry is marked and
     * settled once the save completes (see settleDeferredDeletes).
     * 'cancelled': created here and never persisted, so creating then
     * deleting nets to zero and the entry and its kind-specific payload
     * state are forgotten wholesale. 'pending' covers everything else:
     * a row that exists must receive a DELETE — even when created in
     * this session, a committed skeleton/upload is a row like any other
     * — and a delete of something the database never held stays
     * recorded so {@link hasOutOfScopeDatasets} can arm the guard even
     * though no save can carry it.
     */
    private resolveDelete(
        id: PersistedEntityId,
        kind: DatasetKind
    ): 'deferred' | 'cancelled' | 'pending' {
        const entry = this.trackedFor(id, kind);
        if (entry.phase !== 'idle' && this.registry.getRef(id) === null) {
            entry.phase = 'savingDeleteRequested';
            return 'deferred';
        }
        if (entry.createdInSession && this.registry.getRef(id) === null) {
            this.tracked.delete(id);
            this.forgetPayload(id, kind);
            return 'cancelled';
        }
        entry.deleted = true;
        return 'pending';
    }

    /** Kind-specific state forgotten when a delete cancels a create. */
    private forgetPayload(id: string, kind: DatasetKind): void {
        switch (kind) {
            case 'structureSet': {
                const ssId = id as StructureSetId;
                this.ssBaseline.delete(ssId);
                this.ssLatest.delete(ssId);
                for (const [roiId, owner] of this.roiOwner) {
                    if (owner === ssId) {
                        this.forgetRoi(roiId);
                    }
                }
                break;
            }
            case 'registration':
                this.regBaseline.delete(id as RegistrationId);
                this.regLatest.delete(id as RegistrationId);
                break;
            case 'image':
                this.imgLabelBaseline.delete(id as ImageId);
                this.imgLabelLatest.delete(id as ImageId);
                break;
            case 'dose':
                // datasetMeta is deliberately kept for all kinds (see the
                // image branch of completeSave for the rationale); doses
                // have no other payload state.
                break;
        }
    }

    /** completeSave: a persisted create is a create no longer. */
    private clearCreatedFlag(id: string): void {
        const entry = this.tracked.get(id);
        if (entry) {
            entry.createdInSession = false;
            this.pruneTracked(id);
        }
    }

    /**
     * Unsaved work that no save can carry but that CAN be taken back —
     * derived from the tracked entries instead of latched separately:
     * an imported dose (created in session, no upload path yet), and a
     * delete of an image/dose/registration the database never held
     * (classification returns null for it, but it is still unsaved
     * work the guard must report). Retractable by construction: when
     * the causing entry nets to zero it leaves the map, and the guard
     * disarms with it — a plain latch would pin "Unsaved changes" on
     * the header for the rest of the session, with a primary action
     * that has nothing to save and so can never clear it.
     * Never-persisted structure-set deletes keep the pre-existing
     * behaviour of not arming.
     */
    private hasOutOfScopeDatasets(): boolean {
        for (const [id, entry] of this.tracked) {
            if (
                entry.kind === 'dose' &&
                entry.createdInSession &&
                !entry.deleted
            ) {
                return true;
            }
            if (
                entry.deleted &&
                !entry.createdInSession &&
                entry.kind !== 'structureSet' &&
                this.registry.getRef(id as PersistedEntityId) === null
            ) {
                return true;
            }
        }
        return false;
    }

    /** Ids of `kind` entries matching `test`, in tracking order. */
    private idsOf(
        kind: DatasetKind,
        test: (entry: TrackedEntry) => boolean
    ): string[] {
        const ids: string[] = [];
        for (const [id, entry] of this.tracked) {
            if (entry.kind === kind && test(entry)) {
                ids.push(id);
            }
        }
        return ids;
    }

    // ── Changeset ingestion ───────────────────────────────────────────

    private applyChangeset(changes: readonly DomainChange[]): void {
        if (this.disposed) {
            return;
        }
        for (const change of changes) {
            switch (change.kind) {
                case 'structureSet':
                    this.applyStructureSetChange(change);
                    this.lastChangeAt.set(change.id, this.now());
                    break;
                case 'roi': {
                    this.applyRoiChange(change);
                    // Attribute the edit time to the owning set (known
                    // after apply, which resolves owners for new ROIs).
                    const owner = this.roiOwner.get(change.id);
                    if (owner !== undefined) {
                        this.lastChangeAt.set(owner, this.now());
                    }
                    break;
                }
                case 'registration':
                    this.applyRegistrationChange(change);
                    this.lastChangeAt.set(change.id, this.now());
                    break;
                case 'dose':
                    if (change.type === 'created') {
                        this.markCreated(change.id, 'dose');
                        this.captureDoseMeta(change.id);
                        // Dose upload is not in the save scope yet, so a
                        // created dose produces no pending entry. It is still
                        // unsaved clinical work: hasOutOfScopeDatasets derives
                        // the guard arming from this entry, so the indicator
                        // never reads "All saved" while closing the patient
                        // would discard an imported RTDOSE with no warning.
                        // Retractable — deleting it again removes the entry
                        // and nets to zero.
                    } else if (change.type === 'deleted') {
                        // A created entry is only "never reached the database"
                        // while it has no ref. Once a save has minted one —
                        // including a save still in flight when the user
                        // deletes — cancelling would leave the row behind and
                        // resurrect the dataset on the next reload:
                        // resolveDelete defers exactly that window.
                        this.resolveDelete(change.id, 'dose');
                    } else {
                        // Dose modifications are display settings (scale,
                        // normalisation, colorwash) — outside the save
                        // scope, but still unsaved work for the guard.
                        this.outOfScopeChanges = true;
                    }
                    this.lastChangeAt.set(change.id, this.now());
                    break;
                case 'image':
                    if (change.type === 'created') {
                        this.markCreated(change.id, 'image');
                        this.captureImageMeta(
                            change.id,
                            change.snapshot.modality
                        );
                        // Seed the latest label so a post-save rename has a
                        // baseline to diff against (completeSave promotes it).
                        this.imgLabelLatest.set(
                            change.id,
                            this.liveImageLabel(change.id)
                        );
                        this.lastChangeAt.set(change.id, this.now());
                    } else if (change.type === 'deleted') {
                        // See the dose branch: a ref means the row exists
                        // server-side, whatever the entry's local status, so
                        // the delete has to be sent rather than cancelled —
                        // and mid-save, resolveDelete defers the decision.
                        this.resolveDelete(change.id, 'image');
                        this.lastChangeAt.set(change.id, this.now());
                    } else {
                        // Modified: the only persistable field is the series
                        // label. Export bookkeeping also lands here — the
                        // label compare keeps it a no-op, exactly as before.
                        const label = this.liveImageLabel(change.id);
                        const previous = this.imgLabelLatest.get(change.id);
                        this.imgLabelLatest.set(change.id, label);
                        if (previous !== undefined && previous !== label) {
                            this.captureImageMeta(
                                change.id,
                                change.snapshot.modality
                            );
                            this.lastChangeAt.set(change.id, this.now());
                        }
                    }
                    break;
                case 'plan':
                    // Outside save scope, but still unsaved work: keeps the
                    // leave/close guard armed like the legacy boolean did.
                    this.outOfScopeChanges = true;
                    break;
                case 'patient':
                    // Patient rows carry no user-editable state whose loss
                    // the guard needs to warn about.
                    break;
            }
        }
        this.invalidate();
    }

    private applyRegistrationChange(
        change: Extract<DomainChange, { kind: 'registration' }>
    ): void {
        const id = change.id;
        if (change.type === 'created') {
            this.markCreated(id, 'registration');
        } else if (change.type === 'deleted') {
            // See the dose branch in apply(): only an entry with no ref is
            // genuinely local-only. A create that already committed — or one
            // whose save is still in flight — must produce a delete, or the
            // row survives and the dataset reappears on the next reload.
            if (this.resolveDelete(id, 'registration') === 'cancelled') {
                // Local-only: the database never saw it, and creating then
                // deleting it nets to zero.
                return;
            }
        }
        this.regLatest.set(id, change.snapshot);
    }

    private captureDoseMeta(id: DoseId): void {
        const dose = this.patient.doses.find((d) => d.id === id);
        if (dose) {
            this.datasetMeta.set(id, {
                displayName:
                    dose.plan?.label || dose.dicomDose.doseComment || 'Dose',
                modality: 'RTDOSE',
            });
        }
    }

    private captureImageMeta(id: ImageId, modality: string): void {
        const image = this.patient.images.find((i) => i.id === id);
        if (image) {
            this.datasetMeta.set(id, {
                displayName:
                    image.seriesMetadata.seriesDescription || String(modality),
                modality: String(modality),
            });
        }
    }

    private applyStructureSetChange(
        change: Extract<DomainChange, { kind: 'structureSet' }>
    ): void {
        const id = change.id;
        if (change.type === 'created') {
            this.markCreated(id, 'structureSet');
        } else if (change.type === 'deleted') {
            // StructureSetSaveService mints refs mid-save too (skeleton ->
            // adopt -> content PUT), so a mid-save delete defers exactly
            // like images, doses and registrations; deferred and pending
            // both fall through to record the latest snapshot.
            if (this.resolveDelete(id, 'structureSet') === 'cancelled') {
                // Created and deleted in-session with nothing persisted: never
                // reached the database, nothing to save or show.
                return;
            }
        }
        this.ssLatest.set(id, change.snapshot);
    }

    private applyRoiChange(
        change: Extract<DomainChange, { kind: 'roi' }>
    ): void {
        const id = change.id;
        if (change.type === 'created') {
            const owner = this.findRoiOwner(id);
            if (owner === null) {
                this.unattributedChanges++;
                console.error(
                    'PendingChangesStore: created ROI has no owning ' +
                        'structure set; change kept as unattributed'
                );
                return;
            }
            this.roiOwner.set(id, owner);
            this.createdRoi.add(id);
            this.deletedRoi.delete(id);
        } else if (change.type === 'deleted') {
            if (this.createdRoi.has(id)) {
                this.forgetRoi(id);
                return;
            }
            if (!this.roiOwner.has(id)) {
                this.unattributedChanges++;
                console.error(
                    'PendingChangesStore: deleted ROI has no recorded ' +
                        'owner; change kept as unattributed'
                );
                return;
            }
            this.deletedRoi.add(id);
        } else if (!this.roiOwner.has(id)) {
            // Modified ROI we have never seen: resolve its owner now
            // (e.g. an ROI added by a dicom-level operation that only
            // synced wrappers without a 'created' entry).
            const owner = this.findRoiOwner(id);
            if (owner === null) {
                this.unattributedChanges++;
                console.error(
                    'PendingChangesStore: modified ROI has no owning ' +
                        'structure set; change kept as unattributed'
                );
                return;
            }
            this.roiOwner.set(id, owner);
        }
        this.roiLatest.set(id, change.snapshot);
    }

    // ── Classification ────────────────────────────────────────────────

    private deriveState(): PendingChangesState {
        const byStructureSet = new Map<
            StructureSetId,
            PendingStructureSetChanges
        >();

        const roiChangesBySs = new Map<
            StructureSetId,
            Map<RoiId, PendingRoiChange>
        >();
        for (const [roiId, ssId] of this.roiOwner) {
            const change = this.classifyRoi(roiId);
            if (!change) {
                continue;
            }
            let bucket = roiChangesBySs.get(ssId);
            if (!bucket) {
                bucket = new Map();
                roiChangesBySs.set(ssId, bucket);
            }
            bucket.set(roiId, change);
        }

        const allSsIds = new Set<StructureSetId>([
            ...this.ssBaseline.keys(),
            ...this.ssLatest.keys(),
            ...roiChangesBySs.keys(),
        ]);
        for (const ssId of allSsIds) {
            const entry = this.classifyStructureSet(
                ssId,
                roiChangesBySs.get(ssId) ?? new Map()
            );
            if (entry) {
                byStructureSet.set(ssId, entry);
            }
        }

        const byRegistration = new Map<
            RegistrationId,
            PendingRegistrationChanges
        >();
        const allRegIds = new Set<RegistrationId>([
            ...this.regBaseline.keys(),
            ...this.regLatest.keys(),
        ]);
        for (const regId of allRegIds) {
            const entry = this.classifyRegistration(regId);
            if (entry) {
                byRegistration.set(regId, entry);
            }
        }

        const byImage = new Map<ImageId, PendingDatasetChanges>();
        for (const id of this.idsOf('image', (e) => e.deleted)) {
            const imageId = id as ImageId;
            const entry = this.classifyDeletedDataset('image', imageId);
            if (entry) {
                byImage.set(imageId, entry);
            }
        }
        // Locally created series (reformat/crop): pending until uploaded.
        // Deliberately NOT gated on a registry ref — a new image has none
        // by definition.
        for (const id of this.idsOf(
            'image',
            (e) => e.createdInSession && !e.deleted
        )) {
            const imageId = id as ImageId;
            const meta = this.datasetMeta.get(id);
            byImage.set(imageId, {
                kind: 'image',
                id: imageId,
                displayName: meta?.displayName ?? '',
                modality: meta?.modality ?? '',
                status: 'new',
                labelDirty: false,
                lastModifiedAt: this.lastChangeAt.get(id) ?? null,
            });
        }
        // Persisted series whose label changed (rename PATCH at save).
        for (const [id, latest] of this.imgLabelLatest) {
            if (byImage.has(id) || this.tracked.get(id)?.deleted === true) {
                continue;
            }
            const baseline = this.imgLabelBaseline.get(id);
            if (baseline === undefined || baseline === latest) {
                continue;
            }
            if (this.registry.getRef(id) === null) {
                // Renamed but never persisted (local-only session):
                // nothing the save can do with it.
                continue;
            }
            const meta = this.datasetMeta.get(id);
            byImage.set(id, {
                kind: 'image',
                id,
                displayName: meta?.displayName ?? '',
                modality: meta?.modality ?? '',
                status: 'modified',
                labelDirty: true,
                lastModifiedAt: this.lastChangeAt.get(id) ?? null,
            });
        }
        const byDose = new Map<DoseId, PendingDatasetChanges>();
        for (const id of this.idsOf('dose', (e) => e.deleted)) {
            const doseId = id as DoseId;
            const entry = this.classifyDeletedDataset('dose', doseId);
            if (entry) {
                byDose.set(doseId, entry);
            }
        }

        const hasOutOfScopeChanges =
            this.outOfScopeChanges || this.hasOutOfScopeDatasets();
        return {
            byStructureSet,
            byRegistration,
            byImage,
            byDose,
            hasChanges:
                byStructureSet.size > 0 ||
                byRegistration.size > 0 ||
                byImage.size > 0 ||
                byDose.size > 0 ||
                hasOutOfScopeChanges ||
                this.unattributedChanges > 0,
            hasOutOfScopeChanges,
        };
    }

    private classifyRegistration(
        regId: RegistrationId
    ): PendingRegistrationChanges | null {
        const latest = this.regLatest.get(regId);
        const baseline = this.regBaseline.get(regId);

        if (this.tracked.get(regId)?.deleted === true) {
            if (this.registry.getRef(regId) === null) {
                // Deleted and never persisted (local-only): nothing to save.
                return null;
            }
            const snapshot = latest ?? baseline;
            return {
                registrationId: regId,
                displayName: snapshot?.contentLabel ?? '',
                status: 'deleted',
                matrixDirty: false,
                labelDirty: false,
                descriptionDirty: false,
                approvalDirty: false,
                lastModifiedAt: this.lastChangeAt.get(regId) ?? null,
            };
        }

        const isNew = this.tracked.get(regId)?.createdInSession === true;
        const current = latest ?? baseline;
        if (!current) {
            return null;
        }
        // A deformation field has no upload path, so a new one is never offered for save.
        if (
            isNew &&
            this.patient.approvableRegistrations.find((r) => r.id === regId)
                ?.registrationKind === 'deformable'
        ) {
            return null;
        }

        const matrixDirty =
            baseline !== undefined &&
            latest !== undefined &&
            latest.transformRevision !== baseline.transformRevision;
        // Label and description dirty separately: the label has a rename
        // PATCH; the description has no wire path yet (honest failure).
        const labelDirty =
            baseline !== undefined &&
            latest !== undefined &&
            latest.contentLabel !== baseline.contentLabel;
        const descriptionDirty =
            baseline !== undefined &&
            latest !== undefined &&
            latest.contentDescription !== baseline.contentDescription;
        const approvalDirty =
            baseline !== undefined &&
            latest !== undefined &&
            (latest.approvalStatus !== baseline.approvalStatus ||
                latest.reviewerName !== baseline.reviewerName);

        if (
            !isNew &&
            !matrixDirty &&
            !labelDirty &&
            !descriptionDirty &&
            !approvalDirty
        ) {
            return null;
        }

        return {
            registrationId: regId,
            displayName: current.contentLabel,
            status: isNew ? 'new' : 'modified',
            matrixDirty,
            labelDirty,
            descriptionDirty,
            approvalDirty,
            lastModifiedAt: this.lastChangeAt.get(regId) ?? null,
        };
    }

    private classifyDeletedDataset(
        kind: 'image' | 'dose',
        id: ImageId | DoseId
    ): PendingDatasetChanges | null {
        if (this.registry.getRef(id) === null) {
            // Deleted and never persisted (local-only): nothing to save.
            return null;
        }
        const meta = this.datasetMeta.get(id);
        return {
            kind,
            id,
            displayName: meta?.displayName ?? '',
            modality: meta?.modality ?? (kind === 'dose' ? 'RTDOSE' : ''),
            status: 'deleted',
            labelDirty: false,
            lastModifiedAt: this.lastChangeAt.get(id) ?? null,
        };
    }

    private classifyRoi(roiId: RoiId): PendingRoiChange | null {
        const latest = this.roiLatest.get(roiId);
        const baseline = this.roiBaseline.get(roiId);
        const kinds = new Set<RoiChangeKind>();

        if (this.deletedRoi.has(roiId)) {
            const snapshot = latest ?? baseline;
            if (!snapshot) {
                return null;
            }
            return {
                roiId,
                roiNumber: snapshot.roiNumber,
                displayName: snapshot.name,
                kinds: new Set<RoiChangeKind>(['deleted']),
                color: snapshot.color,
                previousColor: null,
                isPointRoi: snapshot.isPointRoi,
            };
        }
        if (!latest) {
            return null;
        }
        if (this.createdRoi.has(roiId)) {
            kinds.add('added');
        } else if (baseline) {
            if (latest.name !== baseline.name) {
                kinds.add('renamed');
            }
            if (
                latest.color.R !== baseline.color.R ||
                latest.color.G !== baseline.color.G ||
                latest.color.B !== baseline.color.B
            ) {
                kinds.add('colorChanged');
            }
            if (latest.locked !== baseline.locked) {
                kinds.add('lockChanged');
            }
            if (latest.interpretedType !== baseline.interpretedType) {
                kinds.add('typeChanged');
            }
            if (latest.approvalStatus !== baseline.approvalStatus) {
                kinds.add('approvalChanged');
            }
            if (latest.contourRevision !== baseline.contourRevision) {
                kinds.add('contoursEdited');
            }
            if (latest.propertiesRevision !== baseline.propertiesRevision) {
                kinds.add('edited');
            }
        } else {
            // Latest without baseline or created-marker: treat as added
            // (fail safe — it exists now and the database has no row that
            // corresponds to a baseline we know about).
            kinds.add('added');
        }

        if (kinds.size === 0) {
            return null;
        }
        return {
            roiId,
            roiNumber: latest.roiNumber,
            displayName: latest.name,
            kinds,
            color: latest.color,
            previousColor:
                kinds.has('colorChanged') && baseline ? baseline.color : null,
            isPointRoi: latest.isPointRoi,
        };
    }

    private classifyStructureSet(
        ssId: StructureSetId,
        roiChanges: Map<RoiId, PendingRoiChange>
    ): PendingStructureSetChanges | null {
        const latest = this.ssLatest.get(ssId);
        const baseline = this.ssBaseline.get(ssId);

        if (this.tracked.get(ssId)?.deleted === true) {
            if (this.registry.getRef(ssId) === null) {
                // Deleted and never persisted (e.g. loaded from local
                // files without a connector ref): nothing to save.
                return null;
            }
            const snapshot = latest ?? baseline;
            return {
                structureSetId: ssId,
                displayName: snapshot?.displayName ?? '',
                status: 'deleted',
                roiCount: snapshot?.roiCount ?? 0,
                lastModifiedAt: this.lastChangeAt.get(ssId) ?? null,
                contentDirty: false,
                approvalDirty: false,
                rowOnly: false,
                roiChanges: new Map(),
            };
        }

        const isNew = this.tracked.get(ssId)?.createdInSession === true;
        const current = latest ?? baseline;
        if (!current) {
            return null;
        }

        const metadataDirty =
            baseline !== undefined &&
            latest !== undefined &&
            (latest.displayName !== baseline.displayName ||
                latest.description !== baseline.description ||
                // Fail-safe: a differing ROI count with no per-ROI
                // changesets means ROIs were added/removed at the DICOM
                // level behind the wrappers (e.g. a bulk operation that
                // only marked the set). The panel then can't itemize the
                // change, but the set MUST still show as modified and be
                // saved — never "nothing to save" for changed content.
                latest.roiCount !== baseline.roiCount);
        const approvalDirty =
            baseline !== undefined &&
            latest !== undefined &&
            (latest.approvalStatus !== baseline.approvalStatus ||
                latest.reviewerName !== baseline.reviewerName);

        // Everything the RTSTRUCT can hold changes the file: the backend
        // re-derives ROI rows wholesale from the uploaded file, so one content
        // PUT carries all of it. What the file cannot hold does not make it
        // stale, and rewriting it for such a change would mint a new SOP
        // Instance UID for content nobody edited.
        const roiContentChanges = [...roiChanges.values()].filter(
            (c) => !changesOnly(c, ROW_BORNE_KINDS)
        );
        const contentDirty =
            isNew || metadataDirty || roiContentChanges.length > 0;

        if (
            !isNew &&
            !contentDirty &&
            !approvalDirty &&
            roiChanges.size === 0
        ) {
            return null;
        }

        const baselineApproved: DICOMApprovalStatus | null =
            baseline?.approvalStatus ?? null;
        // The metadata PATCH carries the lock whatever the review state, and
        // the colour only while the set is approved: on an unapproved set the
        // file is authoritative for the colour, so a colour edit travels as
        // content like every other DICOM attribute.
        const patchableKinds: readonly RoiChangeKind[] =
            baselineApproved === ApprovalStatus.APPROVED
                ? ['colorChanged', 'lockChanged']
                : ['lockChanged'];
        const rowOnly =
            !approvalDirty &&
            !metadataDirty &&
            !isNew &&
            roiChanges.size > 0 &&
            [...roiChanges.values()].every((c) =>
                changesOnly(c, patchableKinds)
            );

        return {
            structureSetId: ssId,
            displayName: current.displayName,
            status: isNew ? 'new' : 'modified',
            roiCount: current.roiCount,
            lastModifiedAt: this.lastChangeAt.get(ssId) ?? null,
            contentDirty: contentDirty && !rowOnly,
            approvalDirty,
            rowOnly,
            roiChanges,
        };
    }

    // ── Internals ─────────────────────────────────────────────────────

    private captureBaselines(): void {
        for (const ss of this.patient.structureSets) {
            this.baselineStructureSet(ss);
        }
        for (const reg of this.patient.approvableRegistrations) {
            this.baselineRegistration(reg);
        }
        for (const dose of this.patient.doses) {
            this.captureDoseMeta(dose.id);
        }
        for (const image of this.patient.images) {
            this.baselineImage(image);
        }
    }

    private baselineStructureSet(ss: StructureSet): void {
        const snapshot = ss.captureSnapshot();
        this.ssBaseline.set(ss.id, snapshot);
        this.ssLatest.set(ss.id, snapshot);
        for (const roi of ss.rois) {
            const roiSnapshot = roi.captureSnapshot();
            this.roiBaseline.set(roi.id, roiSnapshot);
            this.roiLatest.set(roi.id, roiSnapshot);
            this.roiOwner.set(roi.id, ss.id);
        }
    }

    private baselineRegistration(reg: ApprovableRegistration): void {
        const snapshot = reg.captureSnapshot();
        this.regBaseline.set(reg.id, snapshot);
        this.regLatest.set(reg.id, snapshot);
    }

    private baselineImage(image: Image3D): void {
        this.captureImageMeta(
            image.id,
            String(image.captureSnapshot().modality)
        );
        const label = this.liveImageLabel(image.id);
        this.imgLabelBaseline.set(image.id, label);
        this.imgLabelLatest.set(image.id, label);
    }

    /**
     * The image's current series label from the live aggregate. Image
     * snapshots deliberately exclude free text, so the label is read
     * here instead. Empty string when the image already left the
     * aggregate (a deletion in the same changeset).
     */
    private liveImageLabel(id: ImageId): string {
        const image = this.patient.images.find((i) => i.id === id);
        return image?.seriesMetadata.seriesDescription ?? '';
    }

    private findRoiOwner(roiId: RoiId): StructureSetId | null {
        for (const ss of this.patient.structureSets) {
            if (ss.getRoiById(roiId)) {
                return ss.id;
            }
        }
        return null;
    }

    private forgetRoi(roiId: RoiId): void {
        this.roiBaseline.delete(roiId);
        this.roiLatest.delete(roiId);
        this.roiOwner.delete(roiId);
        this.createdRoi.delete(roiId);
        this.deletedRoi.delete(roiId);
    }

    private invalidate(): void {
        this.cachedState = null;
        this.listeners.forEach((listener) => {
            try {
                listener();
            } catch (error) {
                console.error('PendingChangesStore listener failed:', error);
            }
        });
    }
}

/**
 * The ROI changes the stored RTSTRUCT cannot carry: DICOM defines no attribute
 * for either the per-ROI approval flag or the edit lock, so both live on the
 * database row alone.
 */
const ROW_BORNE_KINDS: readonly RoiChangeKind[] = [
    'approvalChanged',
    'lockChanged',
];

/** Whether every way this ROI changed is one of {@link kinds}. */
function changesOnly(
    change: PendingRoiChange,
    kinds: readonly RoiChangeKind[]
): boolean {
    return [...change.kinds].every((kind) => kinds.includes(kind));
}
